import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { AppConst } from 'constants/appConst';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { CompanyImageSearchItemType } from 'types/api/companyImageSearchItemType';

/**
 * カンパニー画像ギャラリーモーダル
 */
export default function ModalCompanyImageGarrely(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  onImageSelected: (imageKey: string) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState] = useRecoilState(AcctState);

  const { noticeMessageFailure } = useAppState();
  const { postCompanyImageRegister, postCompanyImageSearch, postCompanyImageDelete } =
    useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [keywordState, setKeywordState] = useState('');
  const [sortState, setSortState] = useState<'newest' | 'file' | 'oldest'>('newest');
  const [selectedImageState, setSelectedImageState] = useState<CompanyImageSearchItemType>(null);
  const [companyImagesState, setCompanyImagesState] = useState<CompanyImageSearchItemType[]>([]);

  const inputFileRef = useRef(null);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * モーダルを開いた後の処理
   */
  const onAfterOpen = async () => {
    try {
      setInitialized(true);
      // カンパニー画像検索APIの呼び出し
      const response = await postCompanyImageSearch({
        companyId: acctState.company.id,
      });
      setCompanyImagesState(response.images);
    } finally {
      setInitialized(false);
    }
  };

  /**
   * モーダルを閉じた後の処理
   */
  const onClose = () => {
    props.setOpen(false);
    _initState();
  };

  /**
   * ファイルアップロードボタン押下時の処理
   */
  const onOpenFileUploader = () => {
    inputFileRef.current.click();
  };

  /**
   * ファイル選択時の処理
   */
  const onSelectImage = (imageId: number) => {
    setSelectedImageState(companyImagesState.find(image => image.id === imageId));
  };

  /**
   * ファイル選択ダイアログで選択された画像を読み取ります。
   */
  const onImgFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // ファイルの取得
      const file = e.target.files[0];

      if (AppConst.imageTypes.includes(file.type)) {
        // ファイルの読み込み
        const reader = new FileReader();

        reader.addEventListener('load', () => {
          const image = new Image();
          image.addEventListener('load', async () => {
            try {
              // ローディングの表示
              setProcessing(true);

              // ファイル拡張子を取得
              const extension = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2);

              // ファイルをS3に送信
              const uplodKey = await AppHelper.uploadS3(file, extension);

              // カンパニー画像登録APIの呼び出し;
              const response = await postCompanyImageRegister({
                companyId: acctState.company.id,
                name: file.name,
                imageWidth: image.width,
                imageHeight: image.height,
                imageSize: file.size,
                uploadKey: uplodKey,
              });

              // 画像一覧に追加
              setCompanyImagesState(prevState => [
                {
                  id: response.id,
                  name: file.name,
                  imageKey: response.imageKey,
                  imageWidth: image.width,
                  imageHeight: image.height,
                  imageSize: file.size,
                  createdAt: new Date().getTime(),
                },
                ...prevState,
              ]);
            } finally {
              setProcessing(false);
            }
          });
          image.src = reader.result as string;
        });
        reader.readAsDataURL(e.target.files[0]);
      } else {
        noticeMessageFailure('JPEG、PNG、GIFファイルのみアップロード可能です。');
      }
      // 同じファイルを何度も指定できるように値を初期化
      e.target.value = '';
    }
  };

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = () => {
    if (!selectedImageState) {
      return;
    }
    // 画像の削除
    postCompanyImageDelete({
      companyImageId: selectedImageState.id,
    });

    // ステートの更新
    setCompanyImagesState(prevState =>
      prevState.filter(image => image.id !== selectedImageState.id)
    );
    setSelectedImageState(null);
  };

  /**
   * 決定ボタン押下時の処理
   */
  const onSubmit = () => {
    props.onImageSelected(selectedImageState?.imageKey);
    onClose();
  };

  /**
   * 決定ボタン押下時の処理
   */
  const onSort = (sortType: 'newest' | 'file' | 'oldest') => {
    if (sortType === 'newest') {
      setCompanyImagesState(
        companyImagesState.sort((a, b) => {
          return a.createdAt < b.createdAt ? 1 : -1;
        })
      );
    } else if (sortType === 'oldest') {
      setCompanyImagesState(
        companyImagesState.sort((a, b) => {
          return a.createdAt > b.createdAt ? 1 : -1;
        })
      );
    } else if (sortType === 'file') {
      setCompanyImagesState(
        companyImagesState.sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        })
      );
    }

    setSortState(sortType);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = () => {
    setKeywordState('');
    setSortState('newest');
    setSelectedImageState(null);
    setCompanyImagesState([]);
  };

  /**
   * ファイル名から画像形式を取得します。
   */
  const _getImageType = (fileName: string): string => {
    // ファイル名から拡張子を取得（小文字に変換して比較のため）
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'JPEG';
      case 'png':
        return 'PNG';
      case 'gif':
        return 'GIF';
      case 'bmp':
        return 'BMP';
      case 'webp':
        return 'WEBP';
      default:
        return '';
    }
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={initialized || processing} />

      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        onRequestClose={onClose}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '830px',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        <div className="modal modal-content rounded-4 modal-media">
          <div className="modal-header">
            <h4 className="modal-title">画像ギャラリー</h4>
          </div>
          <div className="modal-body">
            <div className="modal-body-top">
              <div className="form-control-container">
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  ref={inputFileRef}
                  onInput={onImgFileInput}
                />
                <span id="val-img" className="form-control-value"></span>
                <span id="button-upload" className="form-control-btn" onClick={onOpenFileUploader}>
                  新しくファイルをアップロード
                </span>
              </div>

              <div className="d-flex d-flex-search">
                <div className="flex-shrink-0 flex-left">
                  <div className="dropdown">
                    <button
                      className="btn btn-dropdown-media dropdown-toggle"
                      type="button"
                      id="dropdown-media"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {sortState === 'newest'
                        ? '最新順'
                        : sortState === 'oldest'
                        ? '古い順'
                        : 'ファイル名順'}
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="dropdown-media">
                      <li>
                        <a onClick={() => onSort('newest')} className="dropdown-item">
                          最新順
                        </a>
                      </li>
                      <li>
                        <a onClick={() => onSort('oldest')} className="dropdown-item">
                          古い順
                        </a>
                      </li>
                      <li>
                        <a onClick={() => onSort('file')} className="dropdown-item">
                          ファイル名順
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex-grow-1 flex-right">
                  <input
                    type="search"
                    id="search-media"
                    name="search-media"
                    placeholder="ファイル名検索"
                    onChange={e => setKeywordState(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="d-md-flex d-flex-card">
              <div className="flex-shrink-0 flex-item-left company-image-garrely">
                <div className="row row-0">
                  {companyImagesState.map(image => {
                    return (
                      // keywordStateに値が入っている場合、ファイル名にkeywordStateが含まれる画像のみ表示
                      (keywordState == '' || image.name.includes(keywordState)) && (
                        <div
                          key={`modal-company-image-modal-${image.id}`}
                          className={'col-6 col-md-4'}
                        >
                          <a
                            onClick={() => onSelectImage(image.id)}
                            className={`card card-media ${
                              selectedImageState?.id === image.id ? 'active' : ''
                            }`}
                          >
                            <div className="card-body">
                              <div className="img-container">
                                <img
                                  src={AppHelper.getContentImageUrl(image.imageKey)}
                                  className="img-fluid d-block mx-auto mCS_img_loaded"
                                />
                              </div>

                              <h5 className="card-title">{image.name}</h5>

                              <dl className="dl-list-indent">
                                <dt>{_getImageType(image.name)}</dt>

                                <dd>{AppHelper.formatTimeToDate(image.createdAt, 'yyyy/MM/dd')}</dd>
                              </dl>
                            </div>
                          </a>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
              <div className="flex-grow-1 flex-item-right">
                <div className="card card-media-lg">
                  {selectedImageState && (
                    <div className="card-body">
                      <div className="card-body-top">
                        <img
                          src={AppHelper.getContentImageUrl(selectedImageState.imageKey)}
                          className="img-fluid d-block mx-auto"
                        />
                        <h5 className="card-title">{selectedImageState.name}</h5>
                        <ul className="list-unstyled">
                          <li>
                            ファイル形式：
                            {_getImageType(selectedImageState.name)}
                          </li>

                          <li>
                            登録日時：
                            {AppHelper.formatTimeToDate(
                              selectedImageState.createdAt,
                              'yyyy/MM/dd HH:mm'
                            )}
                          </li>

                          <li>
                            サイズ：
                            {`${selectedImageState.imageWidth}px×${selectedImageState.imageHeight}px`}
                            {`（${AppHelper.formatBytes(selectedImageState.imageSize)}）`}
                          </li>
                        </ul>
                      </div>

                      <div className="card-body-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <a
                            href={AppHelper.getContentImageUrl(selectedImageState.imageKey)}
                            className="btn btn-card gray"
                          >
                            ダウンロード
                          </a>

                          <a onClick={onDelete} className="btn btn-delete p-0 hover-img">
                            <img src="/img/icon/trash-can.svg" alt="" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={onClose} type="button" className="btn btn-card btn-card-lg gray">
              キャンセル
            </button>
            <button
              onClick={onSubmit}
              type="button"
              className="btn btn-card btn-card-lg moderate-blue"
              disabled={!selectedImageState}
            >
              この画像に決定
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
}
